import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Spin } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useUser } from "../../contexts/UserContext/UserContext";
import { useToast } from "../../contexts/ToastContext";
import apiClient from "../../helpers/apiClient";
import {
    PAYMENT_METHOD_VALUES,
    PaymentMethodSelector,
    buildAdminPaymentOptions,
    fetchReservationById,
    getDefaultPaymentMethod,
} from "../reservations/lib/reservationPayment";
import CheckInCaptureSection from "../checkin/components/CheckInCaptureSection";
import CheckInSummaryCard from "../checkin/components/CheckInSummaryCard";
import { formatBookedDate } from "../checkin/utils/reservationHelpers";
import {
    completeReservationCheckOut,
    registerCheckOutCashPayment,
} from "./api/checkOutApi";
import useCheckOutSubmit from "./hooks/useCheckOutSubmit";
import {
    buildCheckOutSuccessPath,
    endCheckOutPaymentFlow,
} from "./payment/endCheckOutPaymentFlow";
import endInsuranceCoverageSilently from "./utils/insuranceHelpers";
import useSolaCardPayment from "../../components/payment/useSolaCardPayment";

const CheckOutContent = ({ reservationId }) => {
    const { t } = useTranslation(["checkOut", "checkIn", "reservationPayment"]);
    const { t: tSummary } = useTranslation("checkIn");
    const history = useHistory();
    const toast = useToast();
    const { myCompanyData: companyData } = useUser();
    const { submitCheckOut } = useCheckOutSubmit();
    const { requestSolaCardPayment, solaCardModal } = useSolaCardPayment();

    const defaultPaymentGateway = companyData?.defaultPaymentGateway || "stripe";
    const showCashPayment = companyData?.preferences?.showCashPayment === true;
    const checkOutCaptureRef = useRef(null);

    const [reservation, setReservation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentSystem, setPaymentSystem] = useState(null);

    const paymentOptions = useMemo(
        () =>
            buildAdminPaymentOptions({
                defaultPaymentGateway,
                showCashPayment,
                t,
            }),
        [defaultPaymentGateway, showCashPayment, t],
    );

    const dueAmount = reservation?.invoice?.due || 0;
    const showPaymentSection = dueAmount > 0;

    useEffect(() => {
        setPaymentSystem(null);
    }, [reservationId]);

    useEffect(() => {
        if (!showPaymentSection) {
            setPaymentSystem(null);
            return;
        }
        if (!paymentSystem && paymentOptions.length > 0) {
            setPaymentSystem(getDefaultPaymentMethod(paymentOptions));
        }
    }, [paymentOptions, paymentSystem, showPaymentSection]);

    useEffect(() => {
        let cancelled = false;

        const loadReservation = async () => {
            try {
                setLoading(true);
                const next = await fetchReservationById(reservationId);
                if (cancelled) return;

                if (!next) {
                    setNotFound(true);
                    setReservation(null);
                    setLoading(false);
                    return;
                }

                setReservation({ ...next, id: next.id || reservationId });
                setNotFound(false);
            } catch {
                if (!cancelled) {
                    toast.error({ message: t("loadError") });
                    setNotFound(true);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadReservation();
        return () => {
            cancelled = true;
        };
    }, [toast, reservationId, t]);

    const canConfirmCheckOut = !isSubmitting && (!showPaymentSection || Boolean(paymentSystem));

    const handleConfirmCheckOut = async () => {
        if (!reservation || isSubmitting) return;

        if (showPaymentSection && !paymentSystem) {
            toast.error({ message: t("selectPaymentRequired") });
            return;
        }

        const checkOutData = checkOutCaptureRef.current?.getData();
        if (!checkOutData) {
            toast.error({ message: t("loadError") });
            return;
        }

        setIsSubmitting(true);

        try {
            await submitCheckOut(reservation, checkOutData, toast);

            const { data: updatedRes } = await apiClient.get(`/api/reservation/${reservation.id}`);
            const updatedReservation = updatedRes?.data;
            if (!updatedReservation) {
                throw new Error("Failed to refresh reservation after check-out");
            }

            const nextDueAmount = updatedReservation.invoice?.due || 0;
            const selectedPaymentSystem = paymentSystem || defaultPaymentGateway || "stripe";
            const insuranceReservation = reservation;

            if (nextDueAmount > 0) {
                if (selectedPaymentSystem === PAYMENT_METHOD_VALUES.CASH) {
                    await endInsuranceCoverageSilently(insuranceReservation);
                    await registerCheckOutCashPayment(reservation.id, nextDueAmount);
                    history.push(buildCheckOutSuccessPath(reservation.id));
                    return;
                }

                await endCheckOutPaymentFlow({
                    reservation: updatedReservation,
                    insuranceReservation,
                    dueAmount: nextDueAmount,
                    paymentSystem: selectedPaymentSystem,
                    companyData,
                    history,
                    toast,
                    t,
                    requestSolaCardPayment,
                });
                return;
            }

            await endInsuranceCoverageSilently(insuranceReservation);
            await completeReservationCheckOut(reservation.id);
            history.push(buildCheckOutSuccessPath(reservation.id));
        } catch (error) {
            toast.error({
                message: t("checkOutFailed"),
                description: error?.response?.data?.message || error?.message,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-[320px] items-center justify-center py-12">
                <Spin size="large" tip={t("loadingReservation")} />
            </div>
        );
    }

    if (notFound || !reservation) {
        return (
            <div className="rounded-2xl border border-[#E6EAF2] bg-white p-8 text-center">
                <p className="fontSemi text-lg text-[#101828]">{t("notFoundTitle")}</p>
                <p className="fontRegular mt-2 text-sm text-[#667085]">{t("notFoundBody")}</p>
                <Button type="primary" className="!mt-5 !rounded-xl" onClick={() => history.push("/reservations")}>
                    {t("backToReservations")}
                </Button>
            </div>
        );
    }

    const bookedOn = formatBookedDate(reservation?.createdAt);

    return (
        <div className="flex min-w-0 flex-col gap-3">
            {solaCardModal}
            <div className="mb-2 flex min-w-0 items-start gap-3 sm:items-center sm:gap-4">
                <Button
                    type="text"
                    size="large"
                    icon={<ArrowLeftOutlined className="text-xl sm:text-2xl" />}
                    onClick={() => history.push(`/reservations/${reservationId}`)}
                    className="!h-auto shrink-0 !border-0 !bg-transparent !p-0 !text-[#667085] hover:!text-[#5D5DF6]"
                />
                <div className="min-w-0">
                    <h1 className="fontSemi text-2xl text-[#101828] sm:text-3xl md:text-4xl">{t("pageTitle")}</h1>
                    <p className="fontRegular text-sm text-[#667085]">
                        #{reservationId} · {t("bookedOn")} {bookedOn}
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_380px] xl:gap-8">
                <div className="order-2 min-w-0 space-y-6 sm:space-y-8 lg:order-1">
                    <div className="rounded-2xl border border-[#EEF0F6] bg-white p-4 shadow-[0_8px_30px_rgba(17,24,39,0.06)] sm:p-5 md:p-8">
                        <CheckInCaptureSection
                            ref={checkOutCaptureRef}
                            resetKey={reservationId}
                            flowType="checkout"
                            t={t}
                        />

                        {showPaymentSection ? (
                            <div className="border-t border-[#EEF0F6] pt-8">
                                <PaymentMethodSelector
                                    title={t("paymentTitle")}
                                    subtitle={t("paymentSubtitle")}
                                    options={paymentOptions}
                                    value={paymentSystem}
                                    onChange={setPaymentSystem}
                                />
                            </div>
                        ) : null}
                    </div>

                    <Button
                        type="primary"
                        size="large"
                        block
                        loading={isSubmitting}
                        disabled={!canConfirmCheckOut}
                        onClick={() => {
                            handleConfirmCheckOut();
                        }}
                        className="!h-12 !rounded-xl !px-8 !font-semibold sm:!w-auto sm:!inline-flex">
                        {t("confirmCheckOut")}
                    </Button>
                </div>

                <div className="order-1 lg:order-2">
                    <CheckInSummaryCard reservation={reservation} t={tSummary} />
                </div>
            </div>
        </div>
    );
};

CheckOutContent.propTypes = {
    reservationId: PropTypes.string.isRequired,
};

export default CheckOutContent;
